import { useId, useState, type CSSProperties, type ReactNode } from "react";
import styles from "./TargetBarChart.module.css";
import { Money } from "./Money.js";
import { cx } from "./cx.js";

/**
 * Which side of the target is the good side. A spending budget is a ceiling;
 * a savings target is a floor, and a row that sails past it has done the thing
 * it was set up to do rather than failed at it.
 */
export type TargetGoal = "atMost" | "atLeast";

export interface TargetBar {
  id: string;
  label: string;
  /** What was spent, or set aside — signed the way `Money` expects. */
  valuePence: number;
  /** The line it is measured against. No rule is drawn when there is none. */
  targetPence: number | null;
  /** Defaults to `atMost`: most budgets are a limit. */
  goal?: TargetGoal;
  /** A short state, read alongside the bar rather than instead of it — "£87.60 left". */
  meta?: ReactNode;
  onSelect?: () => void;
}

interface TargetBarChartProps {
  bars: TargetBar[];
  emptyLabel?: string;
  /**
   * Turns every bar into a disclosure: what is behind it opens underneath it,
   * one row at a time. Called only for the row that is open, so a caller that
   * fetches or computes a row's details does it when somebody asks for them.
   */
  renderDetails?: (bar: TargetBar) => ReactNode;
}

/**
 * Named for the verdict rather than for the side of the line the value landed
 * on, because those are not the same thing: £1 past a spending budget is a
 * near miss and £1 past a savings target is the goal met. The colour follows
 * the verdict, so flipping the goal flips the palette without touching it.
 */
type BarStatus = "untargeted" | "spare" | "onTrack" | "nearMiss" | "missed";

/**
 * Where the target's rule line sits, as a percentage of the track.
 *
 * Not 100%. With the target at the far end, every bar that mattered ended at
 * the same place and going past it could only be shown by colour — an
 * over-budget row and an exactly-on-budget one drew the identical full bar.
 * Leaving a quarter of the track past the line makes the distance a length you
 * can see at a glance down the column, before reading a single figure.
 */
const TARGET_AT_PERCENT = 75;

/**
 * How far the wrong side of the target still reads as a near miss.
 *
 * A share of the target rather than a fixed amount of money, because the same
 * few pounds mean different things on a £50 budget and a £2,000 one — and
 * because a proportion needs no adjusting when the same chart draws a year's
 * averages instead of a single month.
 */
const NEAR_MISS_WITHIN = 0.1;

/**
 * How far the *right* side of the target is worth its own colour: half the
 * limit untouched, or half again past the goal. Either is far enough from the
 * line that "on track" undersells it.
 */
const SPARE_BEYOND = 0.5;

const BAR_CLASS: Record<BarStatus, string | undefined> = {
  untargeted: styles.untargeted,
  spare: styles.spare,
  onTrack: styles.onTrack,
  nearMiss: styles.nearMiss,
  missed: styles.missed,
};

/** Only the two verdicts worth pulling out of the muted default. */
const META_CLASS: Partial<Record<BarStatus, string>> = {
  nearMiss: styles.metaNearMiss,
  missed: styles.metaMissed,
};

/**
 * A value against a target, one row per category, on a shared scale — the
 * combined form of what `ProgressBar` draws one at a time. Colour never
 * carries the verdict on its own: the figures, the rule line's position and
 * the meta sentence each say it too, so the four hues only reinforce it.
 */
export function TargetBarChart({
  bars,
  emptyLabel = "Nothing yet",
  renderDetails,
}: TargetBarChartProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const panelPrefix = useId();

  if (bars.length === 0) {
    return (
      <p className={styles.empty} role="status">
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul className={styles.chart}>
      {bars.map((bar) => {
        const target = bar.targetPence !== null && bar.targetPence > 0 ? bar.targetPence : null;
        const value = Math.abs(bar.valuePence);
        const status = statusOf(value, target, bar.goal ?? "atMost");
        /**
         * Each bar against its own target, not one scale shared across every
         * row. A big category's target used to set the scale for every other
         * row too, so a modest budget for something small was squeezed to a
         * sliver — visible mainly as noise, never as "am I over or under".
         * Scaling a row to its own target means every budget reads the same
         * way regardless of size: the rule line is always in the same place,
         * and past it always means over.
         */
        const barStyle: CSSProperties = {
          width: `${String(target === null ? 100 : Math.min((value / target) * TARGET_AT_PERCENT, 100))}%`,
        };

        const content = (
          <>
            <span className={styles.label}>{bar.label}</span>
            <span className={styles.figures}>
              <Money pence={bar.valuePence} tone="none" size="sm" />
              {bar.targetPence !== null && (
                <>
                  {" of "}
                  <Money pence={bar.targetPence} tone="none" size="sm" align={false} />
                </>
              )}
            </span>
            <span className={styles.track}>
              <span className={cx(styles.bar, BAR_CLASS[status])} style={barStyle} />
              {target !== null && (
                <span className={styles.rule} style={{ left: `${String(TARGET_AT_PERCENT)}%` }} />
              )}
            </span>
            {bar.meta && <span className={cx(styles.meta, META_CLASS[status])}>{bar.meta}</span>}
          </>
        );

        const expandable = renderDetails !== undefined;
        const open = expandable && openId === bar.id;
        const panelId = `${panelPrefix}-${bar.id}`;

        return (
          <li key={bar.id} className={styles.row}>
            {expandable || bar.onSelect ? (
              <button
                type="button"
                className={styles.hit}
                onClick={() => {
                  if (expandable) setOpenId(open ? null : bar.id);
                  bar.onSelect?.();
                }}
                aria-expanded={expandable ? open : undefined}
                // Only while it is there to point at.
                aria-controls={open ? panelId : undefined}
              >
                {content}
              </button>
            ) : (
              <span className={styles.hit}>{content}</span>
            )}
            {renderDetails && openId === bar.id && (
              <div id={panelId} className={styles.panel}>
                {renderDetails(bar)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function statusOf(value: number, target: number | null, goal: TargetGoal): BarStatus {
  if (target === null) return "untargeted";

  if (goal === "atLeast") {
    if (value >= target * (1 + SPARE_BEYOND)) return "spare";
    if (value >= target) return "onTrack";
    return value > target * (1 - NEAR_MISS_WITHIN) ? "nearMiss" : "missed";
  }

  if (value > target * (1 + NEAR_MISS_WITHIN)) return "missed";
  if (value > target) return "nearMiss";
  return value < target * (1 - SPARE_BEYOND) ? "spare" : "onTrack";
}
